import { Icon, Input, Menu, Text } from "@ttc3k/trekker";
import { useFormContext, useWatch } from "react-hook-form";
import {
  LinkedToEntity,
  PeopleContextProvider,
  useNotesContext,
} from "shared/contextProviders";
import { NoteFormValues } from "./useNoteCardForm";
import { useMemo, useState } from "react";
import { getMultilingualString } from "shared/utils";
import { useTranslation } from "react-i18next";
import { Box, styled } from "styled-system/jsx";
import { List, Plus, ProfileCircle, Shop, SystemRestart } from "iconoir-react";
import { css, cva } from "styled-system/css";
import { EnumEntityReferenceType } from "gql/generated";
import { useNavigate } from "react-router";
import { useLoggedInAs } from "shared/hooks";
import {
  PeopleTagsContextProvider,
  PersonDetailsModal,
} from "shared/components/organisms";

type LinkedToProps = {
  mode: "view" | "edit" | "archived";
  onNavigateAway?: () => void;
};

export const LinkedTo = ({ mode, onNavigateAway }: LinkedToProps) => {
  const { linkedToEntities, defaultNoteEntity, defaultNoteEntityName } =
    useNotesContext();
  const loggedInAs = useLoggedInAs();

  const [search, setSearch] = useState("");
  const [filteredEntities, setFilteredEntities] = useState(linkedToEntities);
  const [isPeopleModalOpen, setIsPeopleModalOpen] = useState(false);
  const [personModalId, setPersonModalId] = useState<string | null>(null);

  const { t } = useTranslation("core", { keyPrefix: "NOTES" });
  const navigate = useNavigate();

  const { control, setValue } = useFormContext<NoteFormValues>();
  const selectedEntityId = useWatch({
    control,
    name: "entity.id",
  });

  const selectedEntity = useMemo(() => {
    return defaultNoteEntity && defaultNoteEntityName
      ? {
          entityId: defaultNoteEntity._entityId,
          entityType: defaultNoteEntity.type,
          name: defaultNoteEntityName,
        }
      : linkedToEntities.find(entity => entity.entityId === selectedEntityId);
  }, [
    linkedToEntities,
    selectedEntityId,
    defaultNoteEntity,
    defaultNoteEntityName,
  ]);

  const handleSelectEntity = (entityId: string) => {
    const foundEntity = linkedToEntities.find(
      entity => entity.entityId === entityId,
    );
    if (foundEntity) {
      setValue("entity.id", foundEntity.entityId);
      setValue("entity.type", foundEntity.entityType);
      setSearch("");
    }
  };

  const { peopleEntities, operatorEntities, widgetEntities, listEntities } =
    useMemo(() => {
      return {
        peopleEntities: filteredEntities.filter(
          entity => entity.entityType === EnumEntityReferenceType.Person,
        ),
        operatorEntities: filteredEntities.filter(
          entity => entity.entityType === EnumEntityReferenceType.Operator,
        ),
        widgetEntities: filteredEntities.filter(
          entity => entity.entityType === EnumEntityReferenceType.Widget,
        ),
        listEntities: filteredEntities.filter(
          entity => entity.entityType === EnumEntityReferenceType.List,
        ),
      };
    }, [filteredEntities]);

  const handleButtonClick = () => {
    if (selectedEntity) {
      switch (selectedEntity.entityType) {
        case EnumEntityReferenceType.Operator:
          {
            navigate(
              `/app/organization/${loggedInAs?.entityId}/directory/${selectedEntity.entityId}`,
            );
            onNavigateAway?.();
          }
          break;
        case EnumEntityReferenceType.Person:
          setPersonModalId(selectedEntity.entityId);
          setIsPeopleModalOpen(true);
          break;
        case EnumEntityReferenceType.Widget:
          {
            navigate(
              `/app/organization/${loggedInAs?.entityId}/widgets/${selectedEntity.entityId}`,
            );
            onNavigateAway?.();
          }
          break;
        case EnumEntityReferenceType.List:
          {
            navigate(
              `/app/organization/${loggedInAs?.entityId}/custom-lists/${selectedEntity.entityId}`,
            );
            onNavigateAway?.();
          }
          break;
      }
    }
  };

  return (
    <>
      <Menu.Root
        trigger={
          <LinkedToButton
            tabIndex={0}
            disabled={mode === "edit" && !!defaultNoteEntity}
            mode={mode}
            type="button"
            onClick={
              mode !== "edit" && selectedEntity ? handleButtonClick : undefined // If the mode is not in edit and there is a selected entity, handle the button click
            }
          >
            <Icon
              Element={getEntityIconElement(selectedEntity?.entityType)}
              color={mode === "view" ? "icon.highlight.light" : "icon.light"}
              flexShrink={0}
              size={"sm"}
            />
            <Text visual={"smallMedium"} lineClamp={1} wordBreak="break-all">
              {selectedEntity
                ? getMultilingualString(selectedEntity.name)
                : t("ACTIONS.LINK_TO") + "..."}
            </Text>
          </LinkedToButton>
        }
        open={mode !== "edit" && selectedEntity ? false : undefined} // If the mode is not in edit and there is a selected entity, do not allow the menu to open
        _contentProps={{
          className: css({
            overflowY: "auto",
            maxHeight: "400px",
            px: "250",
            pt: "0",
            pb: "250",
            position: "relative",
          }),
        }}
        closeOnSelect
        onSelect={d => handleSelectEntity(d.value)}
      >
        <Box
          bg={"white"}
          position={"sticky"}
          top={0}
          zIndex={1}
          width={"full"}
          pt={"250"}
        >
          <Input
            value={search}
            onChange={e => {
              setSearch(e.target.value);
              const newFilteredEntites = linkedToEntities.filter(entity =>
                getMultilingualString(entity.name)
                  .toLowerCase()
                  .includes(e.target.value.toLowerCase()),
              );
              setFilteredEntities(newFilteredEntites);
            }}
            minW={"300px"}
          />
        </Box>
        {search.length > 0 && (
          <>
            {peopleEntities.length > 0 && (
              <MenuItemGroup
                entities={peopleEntities}
                title={t("LINK_TO.PEOPLE")}
              />
            )}
            {operatorEntities.length > 0 && (
              <MenuItemGroup
                entities={operatorEntities}
                title={t("LINK_TO.OPERATORS")}
              />
            )}
            {widgetEntities.length > 0 && (
              <MenuItemGroup
                entities={widgetEntities}
                title={t("LINK_TO.WIDGETS")}
              />
            )}
            {listEntities.length > 0 && (
              <MenuItemGroup
                entities={listEntities}
                title={t("LINK_TO.LISTS")}
              />
            )}
          </>
        )}
      </Menu.Root>
      <PeopleContextProvider>
        <PeopleTagsContextProvider>
          <PersonDetailsModal
            isOpen={isPeopleModalOpen}
            setIsOpen={setIsPeopleModalOpen}
            personId={personModalId}
            setPersonId={setPersonModalId}
            portal
          />
        </PeopleTagsContextProvider>
      </PeopleContextProvider>
    </>
  );
};

function getEntityIconElement(entityType?: EnumEntityReferenceType) {
  switch (entityType) {
    case EnumEntityReferenceType.Person:
      return ProfileCircle;
    case EnumEntityReferenceType.Widget:
      return SystemRestart;
    case EnumEntityReferenceType.Operator:
      return Shop;
    case EnumEntityReferenceType.List:
      return List;
    default:
      return Plus;
  }
}

function MenuItemGroup({
  entities,
  title,
}: {
  entities: LinkedToEntity[];
  title: string;
}) {
  return (
    <Menu.ItemGroup>
      <Menu.ItemGroupLabel color={"text.light"}>{title}</Menu.ItemGroupLabel>
      {entities.map(e => (
        <Menu.Item
          key={e.entityId}
          value={e.entityId}
          justifyContent={"flex-start"}
          gap={"100"}
        >
          <Icon
            Element={getEntityIconElement(e.entityType)}
            color={"icon.mid"}
          />
          {getMultilingualString(e.name)}
        </Menu.Item>
      ))}
    </Menu.ItemGroup>
  );
}

const linkedToButtonStyles = cva({
  base: {
    display: "flex",
    alignItems: "center",
    gap: "50",
    borderRadius: "full",
    borderWidth: "1px",
    borderColor: "transparent",
    borderStyle: "solid",
    py: "25",
    pl: "100",
    pr: "150",
    width: "fit-content",
    transition: "background 0.2s ease-in-out",

    _disabled: {
      cursor: "default",
    },
  },
  variants: {
    mode: {
      view: {
        bg: "bg.highlight.light",
        color: "text.highlight.dark",
        cursor: "pointer",
      },
      edit: {
        bg: "ink.100",
        color: "text.mid",
        borderColor: "border.light",
        cursor: "pointer",
        _hover: {
          bg: "ink.300",
        },
        _focusVisible: {
          bg: "ink.300",
        },
        _disabled: {
          _hover: {
            bg: "ink.100",
          },
        },
      },
      archived: {
        color: "text.mid",
        borderColor: "border.light",
        opacity: 0.6,
        cursor: "pointer",
      },
    },
  },
});

const LinkedToButton = styled("button", linkedToButtonStyles);
